import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormCategory } from '@dep/common/form-api/enums/form-category.enum';
import { formCategoriesList } from '@dep/common/form-api/form-categories-list';
import { FormDefinitionSectionTable } from '@dep/common/form-api/types/form-definition-section.type';
import {
  FormInstance,
  FormInstanceUpdateInput,
  FormInstanceWithDefinition,
  formInstance,
  formInstanceWithDefinition,
} from '@dep/common/form-api/types/form-instance.type';
import { getFormCategoriesOfRoleAssignment, isFormCategory } from '@dep/common/form-api/utils/form-category.util';
import { RoleRight } from '@dep/common/interfaces';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom, Subject } from 'rxjs';

import { UserService } from '@dep/frontend/users/services/user.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FormInstancesService {
  public dependentSectionSource = new Subject<string>();

  /** Map defining the background color for each form instance status. */
  public readonly STATUS_COLORS = {
    AVAILABLE: '#4F4F4F',
    OPEN: '#4F4F4F',
    DRAFT: 'rgba(0, 141, 252, 0.5)',
    SUBMITTED: '#008DFC',
    APPROVED: '#21A330',
    REJECTED: '#D92121',
    REOPEN: '#D1AA00',
    EXPIRING: '#4F4F4F', // Not used in frontend but still needs a key here to match the `FormInstanceStatus` enum.
  } satisfies Record<Uppercase<string>, string>;

  constructor(
    private readonly logger: NGXLogger,
    private readonly http: HttpClient,
    private readonly userService: UserService,
  ) { }

  /**
   * Get the form categories the current user may access (see `getFormCategoriesOfRoleAssignment` for the rule).
   *
   * @returns Permitted form categories in display order (without duplicates)
   */
  public async getPermittedCategories(): Promise<FormCategory[]> {
    const roles = await this.userService.getRoles();
    const grantedCategories = roles.items.flatMap((role) => getFormCategoriesOfRoleAssignment(role, RoleRight.FORMINSTANCE_READ));

    return formCategoriesList
      .map((formCategory) => formCategory.category)
      .filter((category) => grantedCategories.includes(category));
  }

  /**
   * Check if the current user may access the given form category.
   *
   * @param category - Form category, e. g. from the URL (unknown values are never permitted)
   * @returns `true` if the category is permitted
   */
  public async isCategoryPermitted(category: string | null | undefined): Promise<boolean> {
    if (!isFormCategory(category)) {
      return false;
    }

    return (await this.getPermittedCategories()).includes(category);
  }

  /**
   * Fetch the list of form instances from the form API.
   *
   * @returns An array of `FormInstance` objects representing the list of form instances fetched from the form API
   */
  public async list(): Promise<FormInstance[]> {
    this.logger.debug('Listing form instances');

    try {
      const response = await lastValueFrom(
        this.http.get<FormInstance[]>(
          environment.config.forms.url + '/forms/instances',
          {
            headers: await this.userService.getAuthorizationHeaders(),
          },
        ),
      );

      this.logger.debug('Form instances fetched successfully', response);

      // Parse the API response using `formInstances.parse()` to create validated `FormInstance` objects.
      const validatedInstances = response.map((instance) => formInstance.parse(instance));

      return validatedInstances;
    } catch (error) {
      this.logger.error('Error occurred while fetching all form instances:', error);
      throw error;
    }
  }

  /**
   * Groups a list of form instances by market to enable displaying as a tree view.
   *
   * @param list - The list of form instances to be grouped by market
   * @returns An array of objects where each object represents a market and its associated form instances
   */
  public groupListByMarket(list: FormInstance[]): Omit<GroupedFormInstances, 'isExpanded'>[] {
    this.logger.debug('Grouping form instances by market');

    const groupedByMarket: Record<string, FormInstance[]> = {};

    // Iterating over the array of form instances and put them into the grouped object.
    for (const formInstanceItem of list) {
      const { market } = formInstanceItem;
      if (!Array.isArray(groupedByMarket[market])) {
        groupedByMarket[market] = [];
      }
      groupedByMarket[market].push(formInstanceItem);
    }

    // Converting the grouped data into an array of objects.
    // The resulting array is sorted alphabetically based on the market name.
    const groupedByMarketArray = Object.keys(groupedByMarket)
      .map((market) => ({
        market,
        formInstances: groupedByMarket[market],
      }))
      .sort((a, b) => a.market.localeCompare(b.market));

    this.logger.debug('Form instances grouped by market', groupedByMarketArray);
    return groupedByMarketArray;
  }

  /**
   * Get a single form instance, including the related form definition.
   *
   * @returns `FormInstance` object
   */
  public async getFormInstanceById(id: number): Promise<FormInstanceWithDefinition> {
    this.logger.debug('Getting Form Instance by ID');

    try {
      const response = await lastValueFrom(
        this.http.get<FormInstanceWithDefinition>(
          environment.config.forms.url + '/forms/instances/' + id,
          {
            headers: await this.userService.getAuthorizationHeaders(),
          },
        ),
      );

      this.logger.debug(`Got Form Instance by ID ${id}`, response);

      // Parse the API response using `formInstanceWithDefinition.parse()` to create validated `FormInstance` objects.
      const validatedInstance = formInstanceWithDefinition.parse(response);

      return validatedInstance;
    } catch (error) {
      this.logger.error('Error occurred while trying to get Form Instance:', error);
      throw error;
    }
  }

  /**
   * Update form instance.
   *
   * @param payload - Object with values of the form instance that should be updated
   * @returns Updated form instance
   */
  public async updateFormInstance(payload: FormInstanceUpdateInput, id: number, lockId: string): Promise<FormInstance> {
    this.logger.debug('Updating Form Instance by ID');

    try {
      const response = await lastValueFrom(
        this.http.patch<FormInstanceUpdateInput>(
          environment.config.forms.url + '/forms/instances/' + id,
          payload,
          {
            headers: (await this.userService.getAuthorizationHeaders()).append('dep-form-lock-id', lockId),
          },
        ),
      );

      this.logger.debug(`Got updated Form Instance by ID ${id}`, response);

      // Parse the API response to create a validated `formInstance` object.
      const validatedInstance = formInstance.parse(response);

      return validatedInstance;
    } catch (error) {
      this.logger.error('Error occurred while trying to update Form Instance:', error);
      throw error;
    }
  }

  public async getLockId(formInstanceId: number): Promise<string> {
    this.logger.debug('Getting lock ID for form instance', formInstanceId);

    try {
      const response = await lastValueFrom(
        this.http.post<{ lockId: string }>(
          environment.config.forms.url + '/forms/instances/' + formInstanceId + '/lock',
          {},
          {
            headers: await this.userService.getAuthorizationHeaders(),
          },
        ),
      );

      return response.lockId;
    } catch (error) {
      this.logger.error('Error occurred while trying to update Form Instance:', error);
      throw error;
    }
  }

  public static removePercentageSign(value: string): string {
    if (!value) {
      return '';
    }

    if (value.endsWith('%')) {
      return value.replace('%', '');
    }
    return value;
  }

  /**
   * Triggers an update for dependent sections by notifying any subscribers
   * that the form group values have changed.
   */
  public updateDependentSection(dependentSectionName: string): void {
    this.dependentSectionSource.next(dependentSectionName);
  }

  /**
   * Perform various calculations based on the provided operation type within the `cellCalculation` object.
   *
   * @param cellCalculation - Record of cell calculation
   * @param rowName - The name of the row within the form group
   * @param rowIndex - The index of the current row being processed within the table
   * @param cellIndex - The index of the current cell being processed within the table
   * @param formGroupValues - The form group that holds the current values for all fields across the form's sections
   * @param section - The current section
   * @returns The result of the calculation
   */
  public getCalculation(
    cellCalculation: Record<string, string | number | string[]>,
    rowName: string,
    rowIndex: number,
    cellIndex: number,
    formGroupValues: Record<string, FormGroup>,
    section: FormDefinitionSectionTable,
  ): string {
    if (cellCalculation.operation === 'PERCENTAGE' || cellCalculation.operation === 'DIVISION') {
      let denominator: string | undefined;
      let numerator: string | undefined;
      if (cellCalculation.source === 'ROW') {
        denominator = formGroupValues[section.name]
          .get(String(cellCalculation.denominatorRow))
          ?.get(section.data.tableHeaders.headerTexts[cellIndex + 1].name)
          ?.value;
        numerator = formGroupValues[section.name]
          .get(String(cellCalculation.numeratorRow))
          ?.get(section.data.tableHeaders.headerTexts[cellIndex + 1].name)
          ?.value;
      } else {
        denominator = formGroupValues[section.name]
          .get(rowName)
          ?.get(String(cellCalculation.denominatorColumn))
          ?.value;
        numerator = formGroupValues[section.name]
          .get(rowName)
          ?.get(String(cellCalculation.numeratorColumn))
          ?.value;
      }

      if (
        Number.isNaN(Number(denominator))
        || Number.isNaN(Number(numerator))
        || Number(denominator) === 0
        || Number(numerator) === 0
      ) {
        return '0';
      }

      if (cellCalculation.operation === 'PERCENTAGE') {
        if (cellCalculation.roundingMethod === 'TWO_DECIMAL') {
          return ((Number(numerator) / Number(denominator)) * 100).toFixed(2);
        }

        if (cellCalculation.roundingMethod === 'ROUND') {
          return ((Number(numerator) / Number(denominator)) * 100).toFixed();
        }
      }

      if (cellCalculation.operation === 'DIVISION') {
        const value = Number(numerator) / Number(denominator);

        if (cellCalculation.roundingMethod === 'FLOOR') {
          return String(Math.floor(value));
        }
        return value.toFixed();
      }
    }

    // Calculate percentage based on the sum of numerator and denominator rows.
    if (cellCalculation.operation === 'PERCENTAGE_WITH_NESTED_SUM') {
      if (cellCalculation.source === 'ROW') {
        let numeratorRowsToSum = 0;
        if (Array.isArray(cellCalculation.numeratorRowsToSum)) {
          cellCalculation.numeratorRowsToSum.forEach((row) => {
            const value = formGroupValues[section.name]
              .get(String(row))
              ?.get(section.data.tableHeaders.headerTexts[cellIndex + 1].name)
              ?.value;
            numeratorRowsToSum += Number(value);
          });
        }

        let denominatorRowsToSum = 0;
        if (Array.isArray(cellCalculation.denominatorRowsToSum)) {
          cellCalculation.denominatorRowsToSum.forEach((row) => {
            const value = formGroupValues[section.name]
              .get(String(row))
              ?.get(section.data.tableHeaders.headerTexts[cellIndex + 1].name)
              ?.value;
            denominatorRowsToSum += Number(value);
          });
        }

        if (
          Number.isNaN(Number(denominatorRowsToSum))
          || Number.isNaN(Number(numeratorRowsToSum))
          || Number(denominatorRowsToSum) === 0
          || Number(numeratorRowsToSum) === 0
        ) {
          return '0';
        }

        if (cellCalculation.roundingMethod === 'ROUND') {
          return ((Number(numeratorRowsToSum) / Number(denominatorRowsToSum)) * 100).toFixed();
        }
      }
    }

    // Calculate the sum of values either from the current section's rows (SUM operation)
    // or from rows in a specified section (SUM_FROM_SECTION_ROW operation).
    if (cellCalculation.operation === 'SUM' || cellCalculation.operation === 'SUM_FROM_SECTION_ROW') {
      const sectionName = cellCalculation.operation === 'SUM' ? section.name : cellCalculation.sectionName;

      if (Array.isArray(cellCalculation.rowsToSum)) {
        let sum = 0;
        for (const row of cellCalculation.rowsToSum) {
          const value = formGroupValues[String(sectionName)]
            .get(row)
            ?.get(String(section.data.tableHeaders.headerTexts[cellIndex + 1].name))
            ?.value;

          if (Number.isNaN(Number(value))) {
            return '0';
          }

          sum += Number(value);
        }

        return String(sum);
      }
    }

    // Multiplies valid numeric values from specified rows, returning '0' if any value is invalid or zero,
    // then multiplies the result by 'multiplyByFactor' if it exists, otherwise returns the rowsMultiplicationProduct.
    if (cellCalculation.operation === 'MULTIPLY' && Array.isArray(cellCalculation.rowsToMultiply)) {
      let rowsMultiplicationProduct = 1;
      for (const row of cellCalculation.rowsToMultiply) {
        const value = formGroupValues[section.name]
          .get(row)
          ?.get(String(section.data.tableHeaders.headerTexts[cellIndex + 1].name))
          ?.value;

        // Skip invalid values (NaN, null, undefined) and return early if any value is 0.
        if (Number.isNaN(Number(value)) || Number(value) === 0) {
          return '0';
        }

        rowsMultiplicationProduct *= Number(value);
      }

      const multiplicationResult = cellCalculation.multiplyByFactor
        ? String(rowsMultiplicationProduct * Number(cellCalculation.multiplyByFactor))
        : String(rowsMultiplicationProduct);

      return multiplicationResult;
    }

    // Copies a value from a specified section and row based on the operation type ('COPY_FROM_SECTION_ROW').
    // The value is fetched from the section identified by 'sectionName' and row identified by 'rowName'.
    if (cellCalculation.operation === 'COPY_FROM_SECTION_ROW') {
      const values = formGroupValues[String(cellCalculation.sectionName)];
      const value = values
        .get(String(cellCalculation.rowName))
        ?.get(String(section.data.tableHeaders.headerTexts[cellIndex + 1].name))
        ?.value;

      if (Number.isNaN(Number(value)) || Number(value) === 0) {
        return '0';
      }

      return value;
    }

    // Calculates the percentage based on a denominator value from another section row.
    // The operation 'PERCENTAGE_WITH_DENOMINATOR_FROM_SECTION_ROW' is used to fetch the numerator from the current section
    // and the denominator from a different section and row (specified by 'denominatorSectionName' and 'denominatorRowName').
    if (cellCalculation.operation === 'PERCENTAGE_WITH_DENOMINATOR_FROM_SECTION_ROW') {
      if (cellCalculation.roundingMethod === 'TWO_DECIMAL') {
        const numerator = formGroupValues[section.name]
          .get(String(cellCalculation.numeratorColumn))
          ?.get(String(section.data.tableHeaders.headerTexts[cellIndex + 1].name))
          ?.value;
        const denominator = formGroupValues[String(cellCalculation.denominatorSectionName)]
          .get(String(cellCalculation.denominatorRowName))
          ?.get(String(section.data.tableHeaders.headerTexts[cellIndex + 1].name))
          ?.value;

        if (
          Number.isNaN(Number(denominator))
          || Number.isNaN(Number(numerator))
          || Number(denominator) === 0
          || Number(numerator) === 0
        ) {
          return '0';
        }

        return ((Number(numerator) / Number(denominator)) * 100).toFixed(2);
      }
    }

    // Retrieves a specific value from another section.
    // If the current section has a dependency on another section (as defined by the 'sectionDependencyOn' property),
    // it will retrieve the corresponding value (e.g., GSSN, Location name) from the other section's form group.
    if (cellCalculation.operation === 'COPY_FROM_SECTION_ROW_INDEX') {
      const values = formGroupValues[String(cellCalculation.sectionName)];
      const value = values
        .get(`${String(cellCalculation.rowName + String(rowIndex + 1))}`)
        ?.get(String(section.data.tableHeaders.headerTexts[cellIndex + 1].name))
        ?.value;

      if (!value) {
        return '';
      }

      return value;
    }

    return '0';
  }
}

export enum FormInstanceLockState {
  /** Form not locked (no input possible) */
  NotLocked,
  /** Form locked (inputs can be edited) */
  LockedByCurrentUser,
  /** Form locked by another user (no input possible) */
  LockedByAnotherUser,
  /** Lock requested / open lock popup (no input possible yet) */
  Pending,
  /** Form not editable (no input possible and user cannot request lock) */
  NotEditable,
}

export type FormInstanceState = {
  lock: FormInstanceLockState;
  lockId?: string;
};

export type GroupedFormInstances = {
  market: string;
  formInstances: FormInstance[];
  isExpanded: boolean;
};
